import { canRequestSchoolRole } from '../../../helpers/permission/schoolRoles';
import * as React from 'react';
import propz from 'propz';
import { useState, useEffect } from 'react';
import { REGISTRATION_STAFF_PRESET, RegistrationStaff } from '../Registration';
import { Button } from '../../../components/Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

interface Props {
  staff: RegistrationStaff;
  onStaffClick: (staff: RegistrationStaff) => void;
  onBack: () => void;
  school: any;
}

export function StaffRoleSelector(props: Props) {
  const { staff, onStaffClick, onBack, school } = props;

  const [staffPreset, changeStaffPreset] = useState(staff);

  const isSchoolUnion = propz.get(school, ['kind']) === 'SchoolUnion';
  const schoolName = propz.get(school, ['name'], 'your school');
  const isAdminAllowed = canRequestSchoolRole(school, REGISTRATION_STAFF_PRESET.ADMIN);
  const isManagerAllowed = canRequestSchoolRole(school, REGISTRATION_STAFF_PRESET.MANAGER);
  const isCoachAllowed = canRequestSchoolRole(school, REGISTRATION_STAFF_PRESET.COACH);
  const isTeacherAllowed = canRequestSchoolRole(school, REGISTRATION_STAFF_PRESET.TEACHER);
  const isSltAllowed = canRequestSchoolRole(school, REGISTRATION_STAFF_PRESET.SLT);

  const options = [
    {
      id: 'slt',
      value: REGISTRATION_STAFF_PRESET.SLT,
      isVisible: isSltAllowed,
      title: 'School leadership (SLT)',
      text: 'Read-only school insights and reports. Access requires school administrator approval.'
    },
    {
      id: 'admin',
      value: REGISTRATION_STAFF_PRESET.ADMIN,
      isVisible: isAdminAllowed,
      title: isSchoolUnion ? 'Union admin' : 'School admin',
      text: isSchoolUnion
        ? 'Responsible for organising or hosting competitions for this association.'
        : 'Full access to school settings, users, activities and communication.'
    },
    {
      id: 'manager',
      value: REGISTRATION_STAFF_PRESET.MANAGER,
      isVisible: isManagerAllowed,
      title: 'Manager',
      text: 'Access to day-to-day management tools without core school settings.'
    },
    {
      id: 'coach',
      value: REGISTRATION_STAFF_PRESET.COACH,
      isVisible: isCoachAllowed,
      title: 'Coach',
      text: 'Manage teams, activities and sessions you are responsible for.'
    },
    {
      id: 'teacher',
      value: REGISTRATION_STAFF_PRESET.TEACHER,
      isVisible: isTeacherAllowed,
      title: 'Teacher',
      text: 'Access relevant school activity and student information.'
    }
  ];
  const allowedStaffPresets = options.filter(option => option.isVisible).map(option => option.value);
  const isStaffPresetExist = allowedStaffPresets.indexOf(staffPreset) !== -1;

  useEffect(() => {
    if (typeof staffPreset !== 'undefined' && allowedStaffPresets.indexOf(staffPreset) === -1) {
      changeStaffPreset(undefined);
    }
  }, [isSchoolUnion, staffPreset, isAdminAllowed, isManagerAllowed, isCoachAllowed, isTeacherAllowed, isSltAllowed]);

  return (
    <div className="eRoleSelect">
      <div className="ePermissionIntro">
        <strong>Your role at {schoolName}</strong>
        <span>Choose the option that best matches your responsibilities.</span>
      </div>

      <div className="eRoleCards eRoleCardsStaff">
        {options
          .filter(option => option.isVisible)
          .map(option => {
            const checked = staffPreset === option.value;
            return (
              <button
                key={option.id}
                type="button"
                className={`eStaffRoleCard ${checked ? 'isSelected' : ''}`}
                aria-pressed={checked}
                onClick={() => changeStaffPreset(option.value)}
              >
                <span className="eRoleCardDot" aria-hidden="true">
                  <span />
                </span>
                <span className="eRoleCardContent">
                  <strong>{option.title}</strong>
                  <small>{option.text}</small>
                </span>
              </button>
            );
          })}
      </div>

      <div className="eRegistrationRoleActions">
        <Button onClick={onBack} text="Back" customClass="btn-secondary" />
        <Button
          disabled={!isStaffPresetExist}
          onClick={() => onStaffClick(staffPreset)}
          text={['Continue', <FontAwesomeIcon key="arrow" icon={faArrowRight} />]}
          customClass="eRegistrationSubmitButton"
        />
      </div>
    </div>
  );
}
